// frontend/src/pages/Landing.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Menu,
  X,
  ShieldCheck,
  TrendingUp,
  Sparkles,
  Wallet,
  LineChart,
  BadgeCheck,
  Phone,
  Mail,
  Layers,
  Target,
  BookOpen,
  Calculator,
} from "lucide-react";

import SEO from "../components/SEO";
import TrustBadges from "../components/TrustBadges";
import WhyOjovest from "../components/WhyOjovest";

import EMICalculator from "../components/EMICalculator";
import SIPCalculator from "../components/SIPCalculator";
import LumpsumCalculator from "../components/LumpsumCalculator";
import RetirementPlanner from "../components/RetirementPlanner";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function useOnScreen(options) {
  const ref = useRef(null);
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => setIntersecting(entry.isIntersecting), options);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [options]);

  return [ref, isIntersecting];
}

function useCountUp(active, end, durationMs = 900) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(end * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, end, durationMs]);

  return value;
}

function Container({ className, children }) {
  return <div className={cn("mx-auto w-full max-w-6xl px-4", className)}>{children}</div>;
}

function Section({ id, className, children }) {
  return (
    <section id={id} className={cn("py-16 sm:py-20", className)}>
      {children}
    </section>
  );
}

function Badge({ children }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
      <Sparkles className="h-4 w-4" />
      {children}
    </div>
  );
}

function Button({ as = "button", href, onClick, variant = "primary", className, children, type = "button", disabled }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-60 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-emerald-950 text-white hover:bg-emerald-900"
      : variant === "secondary"
      ? "border border-emerald-900/15 bg-white text-slate-900 hover:bg-emerald-50"
      : "bg-slate-100 text-slate-900 hover:bg-slate-200";

  if (as === "a") {
    return (
      <a href={href} className={cn(base, styles, className)} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={cn(base, styles, className)} type={type} disabled={disabled}>
      {children}
    </button>
  );
}

function Nav() {
  const [open, setOpen] = useState(false);

  const links = useMemo(
    () => [
      { label: "Features", href: "#features" },
      { label: "Services", href: "#services" },
      { label: "Tools", href: "#tools" },
      { label: "Trust", href: "#trust" },
      { label: "FAQ", href: "#faq" },
    ],
    []
  );

  return (
    <header className="sticky top-0 z-50 border-b border-emerald-900/10 bg-white/80 backdrop-blur">
      <Container className="flex h-16 items-center justify-between">
        <a href="#" className="flex items-center gap-2 font-semibold">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-950 text-white shadow-sm">O</div>
          <span>OjoVest</span>
        </a>

        <nav className="hidden items-center gap-6 md:flex">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm font-medium text-slate-700 hover:text-emerald-950">
              {l.label}
            </a>
          ))}
          <Button as="a" href="#start">
            Get started <ArrowRight className="h-4 w-4" />
          </Button>
        </nav>

        <button
          onClick={() => setOpen((s) => !s)}
          className="inline-flex items-center justify-center rounded-xl border border-emerald-900/15 p-2 md:hidden"
          aria-label="menu"
          type="button"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>

      {open && (
        <div className="border-t border-emerald-900/10 bg-white md:hidden">
          <Container className="flex flex-col gap-2 py-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-emerald-50"
              >
                {l.label}
              </a>
            ))}
            <Button as="a" href="#start" className="mt-2" onClick={() => setOpen(false)}>
              Get started <ArrowRight className="h-4 w-4" />
            </Button>
          </Container>
        </div>
      )}
    </header>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <div className="group rounded-2xl border border-emerald-900/10 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
      <div className="flex items-start gap-4">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-950 text-white">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="mt-1 text-sm leading-6 text-slate-600">{desc}</p>
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, desc }) {
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 text-sm font-semibold text-emerald-950">
          {n}
        </div>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-600">{desc}</p>
    </div>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 shadow-sm">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-4 text-left"
        onClick={() => setOpen((s) => !s)}
      >
        <div className="font-semibold text-slate-900">{q}</div>
        <ChevronDown className={cn("h-5 w-5 text-slate-500 transition", open && "rotate-180")} />
      </button>
      {open && <p className="mt-3 text-sm leading-6 text-slate-600">{a}</p>}
    </div>
  );
}

function StatCard({ label, value, suffix }) {
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="text-3xl font-semibold tracking-tight text-emerald-950">
        {value}
        {suffix}
      </div>
      <div className="mt-1 text-sm text-slate-600">{label}</div>
    </div>
  );
}

function Testimonial({ name, role, quote }) {
  return (
    <div className="rounded-2xl border border-emerald-900/10 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-2xl bg-emerald-950 text-white">
          {name?.slice(0, 1)?.toUpperCase()}
        </div>
        <div>
          <div className="text-sm font-semibold">{name}</div>
          <div className="text-xs text-slate-600">{role}</div>
        </div>
      </div>
      <p className="mt-4 text-sm leading-6 text-slate-700">“{quote}”</p>
    </div>
  );
}

export default function Landing() {
  const [startForm, setStartForm] = useState({
    name: "",
    phone: "",
    email: "",
    goal: "wealth",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const [statsRef, statsVisible] = useOnScreen({ threshold: 0.2 });
  const aum = useCountUp(statsVisible, 250, 900);
  const clients = useCountUp(statsVisible, 1200, 1000);
  const rating = useCountUp(statsVisible, 48, 800);

  const features = useMemo(
    () => [
      {
        icon: ShieldCheck,
        title: "Trust-first investing",
        desc: "Clear communication, aligned risk profiles, and a premium experience designed to build confidence.",
      },
      {
        icon: Target,
        title: "Goal-based wealth plans",
        desc: "Retirement, wealth creation, education—strategies tailored to your time horizon.",
      },
      {
        icon: Layers,
        title: "Diversified portfolios",
        desc: "Mutual funds, ETFs, and balanced allocations designed for long-term stability.",
      },
      {
        icon: LineChart,
        title: "Clean reporting & insights",
        desc: "Readable performance, benchmarking, and simple explanations—without overwhelming jargon.",
      },
    ],
    []
  );

  const services = useMemo(
    () => [
      {
        icon: Wallet,
        title: "SIP Planning",
        desc: "Goal-based SIP suggestions with step-up SIP options for faster corpus building.",
      },
      {
        icon: TrendingUp,
        title: "Mutual Fund & ETF Portfolios",
        desc: "Curated baskets by risk profile with periodic rebalancing discipline.",
      },
      {
        icon: ShieldCheck,
        title: "Risk & Asset Allocation",
        desc: "Equity/debt/cash split aligned to your comfort and time horizon.",
      },
      {
        icon: BookOpen,
        title: "Investor Education",
        desc: "Short guides on SIPs, compounding, market cycles, and tax basics to build user confidence.",
      },
    ],
    []
  );

  const steps = useMemo(
    () => [
      {
        n: "01",
        title: "Risk profiling",
        desc: "We map your goals, income, and comfort level to create your investor profile.",
      },
      {
        n: "02",
        title: "Build your plan",
        desc: "Choose SIPs and portfolios aligned with your timeline and target corpus.",
      },
      {
        n: "03",
        title: "Track & rebalance",
        desc: "Monitor progress and rebalance periodically to stay on track.",
      },
    ],
    []
  );

  const faqs = useMemo(
    () => [
      {
        q: "Is OjoVest suitable for beginners?",
        a: "Yes. The experience is designed to be simple first, with deeper details available when you want them.",
      },
      {
        q: "Do you support SIPs and mutual funds?",
        a: "Yes. SIP planning, fund baskets, and portfolios are core parts of a wealth journey.",
      },
      {
        q: "How do you manage risk?",
        a: "We start with profiling, recommend diversification, and suggest periodic rebalancing aligned to your horizon.",
      },
      {
        q: "Can I speak with an advisor?",
        a: "Yes. Use the consultation form and our team will contact you for a guided plan.",
      },
    ],
    []
  );

  const testimonials = useMemo(
    () => [
      { name: "Aman", role: "Founder", quote: "The design feels premium and the flow is simple. It builds trust immediately." },
      { name: "Sana", role: "Working Professional", quote: "Clear explanations and a clean layout. No confusion." },
      { name: "Rohit", role: "Consultant", quote: "Smooth onboarding and easy tools. It feels modern and professional." },
    ],
    []
  );

  async function handleStartSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...startForm,
          source: "landing_form",
          page: "/",
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok) throw new Error(data?.error || "Failed");

      alert(`Thanks ${startForm.name || "there"}! We’ll contact you soon.`);
      setStartForm({ name: "", phone: "", email: "", goal: "wealth", message: "" });
    } catch (err) {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="OjoVest — Premium Wealth Journey"
        description="Plan SIPs, build mutual fund portfolios, and track progress with clean reporting and tools users understand."
        path="/"
      />
      <Nav />

      {/* HERO */}
      <Section className="relative overflow-hidden pt-14">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-100 blur-3xl" />
          <div className="absolute -bottom-24 right-0 h-72 w-72 rounded-full bg-amber-100/60 blur-3xl" />
        </div>

        <Container className="relative">
          <div className="grid items-center gap-10 md:grid-cols-2">
            <div>
              <Badge>Premium wealth management</Badge>

              <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">
                Build wealth with clarity, confidence, and a premium journey.
              </h1>

              <p className="mt-4 max-w-xl text-base leading-7 text-slate-600">
                Plan SIPs, build mutual fund portfolios, and track your progress with clean reporting and tools users actually understand.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Button as="a" href="#start">
                  Get started <ArrowRight className="h-4 w-4" />
                </Button>
                <Button as="a" href="#tools" variant="secondary">
                  Explore tools <Calculator className="h-4 w-4" />
                </Button>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {["Goal-based SIP planning", "Mutual funds & diversified portfolios", "Premium insights & reporting", "Human support when needed"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-emerald-950" />
                    {t}
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <TrustBadges />
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl border border-emerald-900/10 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-950">
                    <BadgeCheck className="h-4 w-4" />
                    Trusted experience
                  </div>
                  <div className="text-xs text-slate-500">Portfolio snapshot</div>
                </div>

                <div className="mt-5 rounded-2xl border border-emerald-900/10 bg-slate-50 p-5">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="text-sm font-semibold">Wealth Plan</div>
                      <div className="mt-1 text-xs text-slate-600">Balanced • Long-term</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-600">Projected growth</div>
                      <div className="text-2xl font-semibold tracking-tight text-emerald-950">+12.4%</div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {[{ k: "Risk", v: "Medium" }, { k: "Horizon", v: "5–10y" }, { k: "Plan", v: "SIP + MF" }].map((x) => (
                      <div key={x.k} className="rounded-xl bg-white p-3 shadow-sm">
                        <div className="text-[11px] text-slate-500">{x.k}</div>
                        <div className="mt-1 text-sm font-semibold">{x.v}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-emerald-900/10 bg-white p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <ShieldCheck className="h-4 w-4 text-emerald-950" />
                      Risk alignment
                    </div>
                    <p className="mt-2 text-xs leading-5 text-slate-600">Strategies matched to your comfort and time horizon.</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-900/10 bg-white p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      <LineChart className="h-4 w-4 text-emerald-950" />
                      Clean reporting
                    </div>
                    <p className="mt-2 text-xs leading-5 text-slate-600">Easy-to-read insights and progress tracking.</p>
                  </div>
                </div>
              </div>

              <div className="pointer-events-none absolute -right-4 -top-4 h-24 w-24 rounded-3xl bg-emerald-950/10 blur-2xl" />
            </div>
          </div>
        </Container>
      </Section>

      {/* WHY OJOVEST */}
      <Section className="bg-white">
        <Container>
          <WhyOjovest />
        </Container>
      </Section>

      {/* FEATURES */}
      <Section id="features" className="bg-emerald-50/40">
        <Container>
          <div className="text-center">
            <Badge>Designed to feel premium</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Everything you need to build wealth</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
              Clean UI, clear language, and a system that prioritizes trust and understanding.
            </p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {features.map((f) => (
              <FeatureCard key={f.title} icon={f.icon} title={f.title} desc={f.desc} />
            ))}
          </div>
        </Container>
      </Section>

      {/* SERVICES */}
      <Section id="services">
        <Container>
          <div className="text-center">
            <Badge>Wealth management services</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">SIPs, Mutual Funds, ETFs & Portfolios</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">Premium brands keep offerings crystal clear—simple and confident.</p>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {services.map((s) => (
              <FeatureCard key={s.title} icon={s.icon} title={s.title} desc={s.desc} />
            ))}
          </div>
        </Container>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="how" className="bg-white">
        <Container>
          <div className="grid items-start gap-10 md:grid-cols-2">
            <div>
              <Badge>Simple process</Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">A clear journey from day one</h2>
              <p className="mt-3 max-w-xl text-base leading-7 text-slate-600">
                OjoVest guides users step-by-step—simple first, with deeper detail when they want it.
              </p>

              <div className="mt-6 space-y-3">
                {["Goal-based SIP planning", "Diversification for stability", "Readable reporting & insights"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-emerald-950" />
                    {t}
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-4">
              {steps.map((s) => (
                <Step key={s.n} n={s.n} title={s.title} desc={s.desc} />
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* TOOLS */}
      <Section id="tools" className="bg-emerald-50/40">
        <Container>
          <div className="text-center">
            <Badge>Tools</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Calculators that build trust</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">Premium wealth platforms provide tools users can try instantly.</p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            <EMICalculator />
            <SIPCalculator />
            <LumpsumCalculator />
            <RetirementPlanner />
          </div>

          <div className="mt-10 rounded-3xl border border-emerald-900/10 bg-white p-7 shadow-sm">
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
              <Sparkles className="h-4 w-4" />
              What to add next for premium wealth
            </div>

            <h3 className="mt-4 text-xl font-semibold">Premium features users expect</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                "Risk Profiling Quiz → Conservative/Balanced/Growth",
                "SIP Builder → goal-based monthly SIP recommendation",
                "Mutual Fund baskets by risk profile + rebalancing",
                "Portfolio dashboard (allocation, returns, benchmark)",
                "Goal tracking (progress bar + timeline projection)",
                "Advisor booking + WhatsApp support integration",
                "Monthly reports (PDF) + email delivery",
                "KYC onboarding + document upload (phase-2)",
              ].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-950" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* TRUST/STATS */}
      <Section id="trust" className="bg-white">
        <Container>
          <div className="text-center">
            <Badge>Trust & transparency</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Premium feel, strong foundation</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">Clean numbers + clean design builds trust faster than long paragraphs.</p>
          </div>

          <div ref={statsRef} className="mt-10 grid gap-4 sm:grid-cols-3">
            <StatCard label="Assets tracked" value={aum} suffix="Cr+" />
            <StatCard label="Users served" value={clients} suffix="+" />
            <StatCard label="Avg rating" value={(rating / 10).toFixed(1)} suffix="/5" />
          </div>
        </Container>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-emerald-50/40">
        <Container>
          <div className="text-center">
            <Badge>Social proof</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">A premium experience users remember</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">Replace these with real client reviews later.</p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {testimonials.map((t) => (
              <Testimonial key={t.name} name={t.name} role={t.role} quote={t.quote} />
            ))}
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-white">
        <Container>
          <div className="text-center">
            <Badge>FAQ</Badge>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">Questions, answered clearly</h2>
            <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">Premium brands keep FAQs short, clear, and confidence-building.</p>
          </div>

          <div className="mx-auto mt-10 grid max-w-3xl gap-3">
            {faqs.map((f) => (
              <FAQItem key={f.q} q={f.q} a={f.a} />
            ))}
          </div>
        </Container>
      </Section>

      {/* START / LEAD FORM */}
      <Section id="start" className="bg-emerald-50/40">
        <Container>
          <div className="grid gap-8 md:grid-cols-2">
            <div className="rounded-3xl border border-emerald-900/10 bg-white p-7 shadow-sm">
              <Badge>Get started</Badge>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight">Request a consultation</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600">This form stores leads in DB (MongoDB) via /api/leads.</p>

              <form className="mt-6 space-y-4" onSubmit={handleStartSubmit}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Name</label>
                    <input
                      value={startForm.name}
                      onChange={(e) => setStartForm((s) => ({ ...s, name: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-200"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-700">Phone</label>
                    <input
                      value={startForm.phone}
                      onChange={(e) => setStartForm((s) => ({ ...s, phone: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-200"
                      placeholder="+91..."
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-700">Email</label>
                  <input
                    type="email"
                    value={startForm.email}
                    onChange={(e) => setStartForm((s) => ({ ...s, email: e.target.value }))}
                    className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="you@email.com"
                    required
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-slate-700">Goal</label>
                    <select
                      value={startForm.goal}
                      onChange={(e) => setStartForm((s) => ({ ...s, goal: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-200"
                    >
                      <option value="wealth">Wealth Growth</option>
                      <option value="retirement">Retirement</option>
                      <option value="tax">Tax Saving</option>
                      <option value="custom">Custom Goal</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <Button className="w-full" type="submit" disabled={submitting}>
                      {submitting ? "Submitting..." : "Submit"} <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-700">Message (optional)</label>
                  <textarea
                    value={startForm.message}
                    onChange={(e) => setStartForm((s) => ({ ...s, message: e.target.value }))}
                    className="mt-1 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-emerald-200"
                    rows={4}
                    placeholder="Tell us what you want to achieve..."
                  />
                </div>

                <p className="text-xs text-slate-500">
                  By submitting, you agree to be contacted by OjoVest. See{" "}
                  <a href="/privacy" className="underline">Privacy Policy</a>.
                </p>
              </form>
            </div>

            <div className="rounded-3xl bg-emerald-950 p-7 text-white shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-white/90">
                <Sparkles className="h-4 w-4" />
                Premium wealth platform
              </div>

              <h3 className="mt-4 text-3xl font-semibold tracking-tight">Make finance feel simple.</h3>
              <p className="mt-3 text-sm leading-6 text-white/80">
                Your website should build trust instantly: clean typography, clear process, and an easy next step.
              </p>

              <div className="mt-6 space-y-3">
                {["SIP plans & mutual fund portfolios", "Goal tracking and progress projection", "Benchmark comparisons", "Monthly reporting"].map((t) => (
                  <div key={t} className="flex items-center gap-2 text-sm text-white/90">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                    {t}
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-3 rounded-2xl bg-white/10 p-5">
                <div className="flex items-center gap-2 text-sm text-white/90">
                  <Phone className="h-4 w-4" />
                  <span>Support: +91-XXXXXXXXXX</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-white/90">
                  <Mail className="h-4 w-4" />
                  <span>hello@ojovest.com</span>
                </div>
                <div className="text-xs text-white/60">Replace with your real contact details.</div>
              </div>

              <div className="mt-6">
                <Button as="a" href="#tools" variant="secondary" className="w-full">
                  Explore tools <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* INDIA-READY LEGAL FOOTER */}
      <footer className="border-t border-emerald-900/10 py-10">
        <Container className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 font-semibold">
              <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-950 text-white">O</div>
              <span>OjoVest</span>
            </div>
            <p className="max-w-sm text-xs text-slate-500 leading-5">
              Disclaimer: OjoVest is a platform for information & lead capture. Nothing on this website is financial advice.
              Investment products are subject to market risks. Read all scheme-related documents carefully.
            </p>
            <p className="text-xs text-slate-500">
              Grievance: <span className="font-medium">grievance@ojovest.com</span> (replace with real)
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-slate-600">
            <a className="hover:text-emerald-950" href="/privacy">Privacy</a>
            <a className="hover:text-emerald-950" href="/terms">Terms</a>
            <a className="hover:text-emerald-950" href="/refund">Refund</a>
            <a className="hover:text-emerald-950" href="/contact">Contact</a>
          </div>

          <div className="text-xs text-slate-500">© {new Date().getFullYear()} OjoVest. All rights reserved.</div>
        </Container>
      </footer>
    </div>
  );
}
